import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { DefaultButton, Dialog, DialogFooter, DialogType, IconButton, mergeStyles, PrimaryButton, Stack, Text, } from "@fluentui/react";
import { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router";
import { useRootContext } from "../../library/providers/root/RootContextProvider";
import { useCommercialContextDispatch } from "../../reducers/CommercialContext/CommercialContextProvider";
import { CustomerDataContextProvider } from "../../reducers/CustomerSearchContext/CustomerSearchContextProvider";
import { ConfidenceCallTypeTab } from "./ConfidenceCallTypeTab";
import { ReviewSubmitTab } from "./ReviewSubmitTab";
import { createConfidenceCallSubmissionFingerprint, resolveSubmissionAttempt, } from "./submissionAttempt";
// Header styles
const headerContainerStyle = mergeStyles({
    backgroundColor: "#f3f2f1",
    borderBottom: "1px solid #d1d1d1",
    padding: "16px 24px",
    minHeight: "80px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
});
const headerTitleStyle = mergeStyles({
    fontSize: "24px",
    fontWeight: "600",
    color: "#323130",
    margin: 0,
});
const contentContainerStyle = mergeStyles({
    padding: "24px",
    backgroundColor: "#ffffff",
    minHeight: "calc(100vh - 160px)",
});
export const CustomerConfidenceCall = () => {
    const { isConfigurationSet } = useRootContext();
    const commercialContextDispatch = useCommercialContextDispatch();
    const hasLoadedCommercialMetadata = useRef(false);
    const [showReviewTab, setShowReviewTab] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [callTypeNextClicked] = useState(0);
    const [reviewSubmitNextClicked, setReviewSubmitNextClicked] = useState(0);
    const [clientSubmissionId, setClientSubmissionId] = useState("");
    const submissionAttemptRef = useRef();
    const submissionInFlightRef = useRef(false);
    // Store basic info data to pass to review tab
    const [customerName, setCustomerName] = useState("");
    // Cancel confirmation dialog
    const [showCancelDialog, setShowCancelDialog] = useState(false);
    // Track if we're coming back from review tab to restore state
    const [isComingBackFromReview, setIsComingBackFromReview] = useState(false);
    // Form data states lifted from individual tabs
    const [customerInformationData, setCustomerInformationData] = useState({
        customerTpid: "",
        customerName: "",
        customerCountry: "",
        customerCountryId: "",
        customerCountryAlpha2: "",
        customerCountryAlpha3: "",
        customerCountryIsEU: false,
        customerMsSalesId: "",
        customerSegment: "",
        isManualCustomerEntry: false,
        azureSubscriptionId: "",
        azureSubscriptionSelection: undefined,
        manualAzureSubscriptionId: "",
        subscriptionCandidates: [],
        subscriptionContextKey: "",
        selectedIncidents: [],
        selectedIncidentOutageMap: {},
        selectedSupportRequestNumbers: [],
        supportTicketTitleMap: {},
    });
    const [productComponentData, setProductComponentData] = useState({
        workloads: [],
        selectedProducts: [],
        components: [],
    });
    const [callTypeData, setCallTypeData] = useState({
        type: "",
        typeKey: "",
    });
    const [technicalDepthData, setTechnicalDepthData] = useState("");
    const [contextRationaleData, setContextRationaleData] = useState({
        technicalDepth: "",
        reasons: [],
    });
    const history = useHistory();
    useEffect(() => {
        if (isConfigurationSet && !hasLoadedCommercialMetadata.current) {
            hasLoadedCommercialMetadata.current = true;
            commercialContextDispatch.getCommercialMetadata();
        }
    }, [isConfigurationSet, commercialContextDispatch]);
    const onCancel = () => {
        if (isLoading) {
            return;
        }
        setShowCancelDialog(true);
    };
    const handleCancelConfirm = () => {
        if (isLoading) {
            return;
        }
        setShowCancelDialog(false);
        history.push("/escalation");
    };
    const handleCancelDismiss = () => {
        // Just close the dialog and stay on the current page
        setShowCancelDialog(false);
    };
    const moveToNextTabFromCallType = (isValid, data) => {
        if (isValid && data) {
            const nextCustomerCountry = data.customerCountry || customerInformationData.customerCountry || "";
            // Store all the consolidated data from ConfidenceCallTypeTab
            setCustomerName(data.customerName || "");
            // Store data for review tab
            setCustomerInformationData({
                customerTpid: data.customerTpid || "",
                customerName: data.customerName || "",
                customerCountry: nextCustomerCountry,
                customerCountryId: data.customerCountryId || "",
                customerCountryAlpha2: data.customerCountryAlpha2 || "",
                customerCountryAlpha3: data.customerCountryAlpha3 || "",
                customerCountryIsEU: data.customerCountryIsEU || false,
                customerMsSalesId: data.customerMsSalesId || "",
                customerSegment: data.customerSegment || "",
                isManualCustomerEntry: data.isManualCustomerEntry || false,
                azureSubscriptionId: data.azureSubscriptionId || "",
                azureSubscriptionSelection: data.azureSubscriptionSelection,
                manualAzureSubscriptionId: data.manualAzureSubscriptionId || "",
                subscriptionCandidates: data.subscriptionCandidates || [],
                subscriptionContextKey: data.subscriptionContextKey || "",
                selectedIncidents: data.selectedIncidents || [],
                selectedIncidentOutageMap: data.selectedIncidentOutageMap || {},
                selectedSupportRequestNumbers: data.selectedSupportRequestNumbers || [],
                supportTicketTitleMap: data.supportTicketTitleMap || {},
            });
            setCallTypeData({
                type: data.type || "",
                typeKey: data.typeKey || "",
            });
            setTechnicalDepthData(data.technicalDepth || "");
            setContextRationaleData({
                technicalDepth: data.technicalDepth || "",
                reasons: data.escalationReasons || [],
                desiredOutcome: data.desiredOutcome || "",
                businessImpact: data.businessImpact || "",
                stakeholders: data.stakeholders || [],
                escalationReasonSummary: data.escalationReasonSummary || "",
            });
            setProductComponentData({
                workloads: data.workloads || [],
                selectedProducts: data.selectedProducts || [],
                components: data.components || [],
            });
            // Move to Review & Submit tab
            setShowReviewTab(true);
            setIsComingBackFromReview(false); // Reset the flag
        }
        setIsLoading(false);
    };
    const handleCustomerDataLoaded = (customerTpid, loadedCustomerName, loadedCustomerCountry, loadedCustomerCountryId, loadedCustomerCountryAlpha2, loadedCustomerCountryAlpha3, loadedCustomerCountryIsEU, loadedCustomerMsSalesId, loadedCustomerSegment) => {
        setCustomerName(loadedCustomerName);
        setCustomerInformationData((prev) => (Object.assign(Object.assign({}, prev), { customerTpid, customerName: loadedCustomerName, customerCountry: loadedCustomerCountry || prev.customerCountry, customerCountryId: loadedCustomerCountryId || prev.customerCountryId, customerCountryAlpha2: loadedCustomerCountryAlpha2 || prev.customerCountryAlpha2, customerCountryAlpha3: loadedCustomerCountryAlpha3 || prev.customerCountryAlpha3, customerCountryIsEU: loadedCustomerCountryIsEU !== undefined
                ? loadedCustomerCountryIsEU
                : prev.customerCountryIsEU, customerMsSalesId: loadedCustomerMsSalesId || "", customerSegment: loadedCustomerSegment || prev.customerSegment })));
    };
    const moveToNextTabFromReviewSubmit = () => {
        submissionInFlightRef.current = false;
        setIsLoading(false);
    };
    const submitReviewedConfidenceCall = () => {
        if (submissionInFlightRef.current) {
            return;
        }
        const payloadFingerprint = createConfidenceCallSubmissionFingerprint({
            basicInfo: customerInformationData,
            productComponent: productComponentData,
            callType: callTypeData,
            technicalDepth: technicalDepthData,
            contextRationale: contextRationaleData,
        });
        const attempt = resolveSubmissionAttempt(submissionAttemptRef.current, payloadFingerprint);
        submissionAttemptRef.current = attempt;
        submissionInFlightRef.current = true;
        setClientSubmissionId(attempt.clientSubmissionId);
        setIsLoading(true);
        setReviewSubmitNextClicked((previous) => previous + 1);
    };
    const goBackToCallType = () => {
        setIsComingBackFromReview(true);
        setShowReviewTab(false);
    };
    return (_jsx(CustomerDataContextProvider, { children: _jsxs(Stack, { children: [_jsxs("div", Object.assign({ className: headerContainerStyle }, { children: [_jsx("div", { children: _jsx(Text, Object.assign({ className: headerTitleStyle }, { children: customerName
                                    ? `Azure Confidence Calls & Technical (non-quota) escalation - ${customerName}`
                                    : "Azure Confidence Calls & Technical (non-quota) escalation" })) }), _jsx(IconButton, { iconProps: { iconName: "Cancel" }, title: "Close", ariaLabel: "Close", onClick: onCancel, disabled: isLoading, styles: {
                                root: {
                                    color: "#605e5c",
                                    backgroundColor: "transparent",
                                },
                                rootHovered: {
                                    backgroundColor: "#f3f2f1",
                                },
                            } })] })), _jsx("div", Object.assign({ className: contentContainerStyle }, { children: !showReviewTab ? (_jsx(ConfidenceCallTypeTab, { callTypeNextClicked: callTypeNextClicked, moveToNextTab: moveToNextTabFromCallType, onCustomerDataLoaded: handleCustomerDataLoaded, savedData: isComingBackFromReview
                            ? {
                                customerTpid: customerInformationData.customerTpid,
                                customerName: customerInformationData.customerName,
                                customerCountry: customerInformationData.customerCountry,
                                customerCountryId: customerInformationData.customerCountryId,
                                customerCountryAlpha2: customerInformationData.customerCountryAlpha2,
                                customerCountryAlpha3: customerInformationData.customerCountryAlpha3,
                                customerCountryIsEU: customerInformationData.customerCountryIsEU,
                                customerMsSalesId: customerInformationData.customerMsSalesId,
                                customerSegment: customerInformationData.customerSegment,
                                isManualCustomerEntry: customerInformationData.isManualCustomerEntry,
                                selectedIncidents: customerInformationData.selectedIncidents,
                                selectedIncidentOutageMap: customerInformationData.selectedIncidentOutageMap,
                                selectedSupportRequestNumbers: customerInformationData.selectedSupportRequestNumbers,
                                supportTicketTitleMap: customerInformationData.supportTicketTitleMap,
                                azureSubscriptionId: customerInformationData.azureSubscriptionId,
                                azureSubscriptionSelection: customerInformationData.azureSubscriptionSelection,
                                manualAzureSubscriptionId: customerInformationData.manualAzureSubscriptionId,
                                subscriptionCandidates: customerInformationData.subscriptionCandidates,
                                subscriptionContextKey: customerInformationData.subscriptionContextKey,
                                type: callTypeData.type,
                                typeKey: callTypeData.typeKey,
                                technicalDepth: technicalDepthData,
                                escalationReasons: contextRationaleData.reasons,
                                desiredOutcome: contextRationaleData.desiredOutcome || "",
                                businessImpact: contextRationaleData.businessImpact || "",
                                stakeholders: contextRationaleData.stakeholders || [],
                                escalationReasonSummary: contextRationaleData.escalationReasonSummary || "",
                                workloads: productComponentData.workloads,
                                selectedProducts: productComponentData.selectedProducts,
                                components: productComponentData.components,
                            }
                            : undefined, expandAllSections: isComingBackFromReview })) : (_jsx(ReviewSubmitTab, { reviewSubmitNextClicked: reviewSubmitNextClicked, moveToNextTab: moveToNextTabFromReviewSubmit, clientSubmissionId: clientSubmissionId, onPrevious: goBackToCallType, onSubmit: submitReviewedConfidenceCall, isSubmitting: isLoading, basicInfo: customerInformationData, productComponent: productComponentData, callType: callTypeData, technicalDepth: technicalDepthData, contextRationale: contextRationaleData })) })), _jsx(Dialog, Object.assign({ hidden: !showCancelDialog, onDismiss: handleCancelDismiss, dialogContentProps: {
                        type: DialogType.normal,
                        title: "Cancel Azure Confidence Calls & Technical (non-quota) escalation",
                        subText: "Are you sure you want to cancel? Any unsaved progress will be lost.",
                    }, modalProps: {
                        isBlocking: true,
                        styles: { main: { maxWidth: 450 } },
                    } }, { children: _jsxs(DialogFooter, { children: [_jsx(PrimaryButton, { onClick: handleCancelConfirm, text: "Yes, Cancel", disabled: isLoading }), _jsx(DefaultButton, { onClick: handleCancelDismiss, text: "No, Stay Here" })] }) }))] }) }));
};
export default CustomerConfidenceCall;
